// Photos, videos and documents picked offline (BL-2447, ADR 066 — Offline Mode § 4.3): a file is
// staged on the device and named by a placeholder path until the queue uploads it, a Save's change
// lists become one queue entry per item, and the device's copy of the lot shows them meanwhile.
import type { InventoryMediaItem } from '@auctogon/core/inventory';
import type { LotCreateRequestPayload } from '@auctogon/core-admin/schemas/item-form';
import type { StagedDocumentRef, StagedMediaRef } from '@auctogon/core-admin/schemas';
import type { OfflineDeltaLotDto } from '@auctogon/core-admin/schemas/responses/offline-sync';
import type { ExternalDocument } from '@auctogon/db/types/jsonb';

import { applyLotDocumentChanges, applyLotMediaChanges } from '../lots/lot-attachment-edit';
import { buildQueuedEntry, type ChangeQueueJson, type ChangeQueueLabel, type QueuedEntryInput } from './change-queue-entry';

/**
 * The placeholder a staged file's `storagePath` holds until it uploads. It reads as a URL scheme,
 * so the server's storage-path guard refuses it should one ever reach a door.
 */
const STAGED_PATH_PREFIX = 'offline-staged:';

/**
 * @param fileId - the staged file's id on the device.
 * @returns the placeholder storage path that names it.
 */
export function offlineStagedPath(fileId: string): string {
  return `${STAGED_PATH_PREFIX}${fileId}`;
}

/**
 * @param path - a media item's or document's storage path.
 * @returns the staged file it names, or null for a real storage path (or none).
 */
export function stagedFileIdOf(path: string | null | undefined): string | null {
  if (!path?.startsWith(STAGED_PATH_PREFIX)) return null;
  const id = path.slice(STAGED_PATH_PREFIX.length);
  return id.length > 0 ? id : null;
}

/**
 * Whether a still is on the phone: an object URL, the only form the offline reads hand an on-phone
 * still out in (the cached-thumbnail and staged-preview pools). Any other URL is remote, and offline
 * it is never fetched (ADR 066 — Offline Mode § 2). The one check the full-screen viewer and the
 * media tiles share (BL-2565, BUG-2138).
 *
 * @param url - a photo's `url` or a video's `thumbnail`.
 * @returns true when the still can be shown offline.
 */
export function isOnDevice(url: string | null | undefined): url is string {
  return url != null && url.startsWith('blob:');
}

/**
 * @param fileId - a staged video's id.
 * @returns the id its poster frame is staged under, beside it.
 */
export function stagedPosterIdOf(fileId: string): string {
  return `${fileId}-poster`;
}

/**
 * @param fileId - a staged video's id.
 * @returns the id the thumbnail derived from its poster is staged under, beside it (BL-2684): the hand-off makes it,
 * because the native lane has no canvas and the server wants a JPEG of at most 100 KiB.
 */
export function stagedThumbIdOf(fileId: string): string {
  return `${fileId}-thumb`;
}

/** A photo or video as the drawer's change list carries it. */
export type OfflineMediaAdd = Pick<InventoryMediaItem, 'id' | 'type' | 'sortOrder' | 'isCover'> &
  Partial<Pick<InventoryMediaItem, 'caption' | 'storagePath' | 'posterStoragePath' | 'url' | 'thumbnail'>>;

/** A document as the drawer's change list carries it. */
export type OfflineDocumentAdd = Pick<ExternalDocument, 'id' | 'name' | 'type' | 'uploadedAt'> &
  Partial<Pick<ExternalDocument, 'storagePath' | 'url'>>;

/** What a Save's or a create's photo and document changes amount to. */
export interface OfflineAttachmentChanges {
  media: { add: OfflineMediaAdd[]; remove: string[] };
  documents: { add: OfflineDocumentAdd[]; remove: string[] };
}

/** The lot every entry of one write names, how the queue labels it, and what it waits on. */
export interface AttachmentEntryContext {
  lotId: string;
  label: ChangeQueueLabel;
  /** The lot's queued create, for a lot made offline; else null. */
  dependsOn: string | null;
  /** Mints an entry id for a change with no staged file to own it. */
  newId: () => string;
}

/** A path the server may store: a staged file's placeholder never is. */
function realPath(path: string | undefined): string | null {
  return path && stagedFileIdOf(path) == null ? path : null;
}

function documentAddEntry(ctx: AttachmentEntryContext, doc: OfflineDocumentAdd): QueuedEntryInput {
  const stagedFileId = stagedFileIdOf(doc.storagePath);
  const storagePath = realPath(doc.storagePath);
  const body: { [key: string]: ChangeQueueJson } = {
    id: doc.id,
    name: doc.name,
    type: doc.type,
    uploadedAt: doc.uploadedAt,
    ...(storagePath ? { storagePath } : {}),
  };
  return buildQueuedEntry({
    id: stagedFileId ?? ctx.newId(),
    op: 'lot.document.add',
    call: { params: { id: ctx.lotId }, query: null, body },
    label: ctx.label,
    lotId: ctx.lotId,
    dependsOn: ctx.dependsOn,
    stagedFileId,
  });
}

function removeEntry(ctx: AttachmentEntryContext, input: { op: 'lot.document.remove'; id: string }): QueuedEntryInput {
  const param = 'documentId';
  return buildQueuedEntry({
    id: ctx.newId(),
    op: input.op,
    call: { params: { id: ctx.lotId, [param]: input.id }, query: null, body: null },
    label: ctx.label,
    lotId: ctx.lotId,
    dependsOn: ctx.dependsOn,
  });
}

/**
 * One queue entry per document added or removed (the per-item doors, BL-2338): removes first, then
 * adds, the order the server folds a Save's change list in. Photos and videos are part of the lot's
 * desired state instead (BL-2815).
 *
 * @param ctx - the lot, its label, what the entries wait on and an id minter.
 * @param changes - the photos and documents added and removed.
 * @returns the entries, in replay order.
 */
export function attachmentQueueEntries(ctx: AttachmentEntryContext, changes: OfflineAttachmentChanges): QueuedEntryInput[] {
  return [
    ...changes.documents.remove.map((id) => removeEntry(ctx, { op: 'lot.document.remove', id })),
    ...changes.documents.add.map((doc) => documentAddEntry(ctx, doc)),
  ];
}

/** A staged photo as the device's copy keeps it: no URL (a blob URL dies with the page). */
function deviceMediaItem(item: OfflineMediaAdd): InventoryMediaItem {
  const { url: _url, thumbnail: _thumbnail, ...rest } = item;
  return { ...rest, url: '' };
}

function deviceDocument(doc: OfflineDocumentAdd): ExternalDocument {
  return { ...doc, url: '' };
}

/**
 * The device's copy of a lot after its photo and document changes, folded by the same per-item
 * edits the server runs, onto the lot form's detail and the lists' row alike: a staged photo shows
 * on the lists at once (BUG-2081, reversing BL-2447's Q9), its cover read from the list-sized
 * thumbnail the Save writes.
 *
 * @param input.lot - the cached lot.
 * @param input.changes - the photos and documents added and removed.
 * @returns the lot, or `cap` when an add would pass the per-lot limit (the server would refuse it).
 */
export function applyLotAttachmentOverlay(input: {
  lot: OfflineDeltaLotDto;
  changes: OfflineAttachmentChanges;
}): { ok: true; lot: OfflineDeltaLotDto } | { ok: false; code: 'cap' } {
  const { lot, changes } = input;
  const mediaChanges = { add: changes.media.add.map(deviceMediaItem), remove: changes.media.remove };
  const media = applyLotMediaChanges<InventoryMediaItem>(lot.detail.media, mediaChanges);
  const documents = applyLotDocumentChanges<ExternalDocument>(lot.detail.documents, {
    add: changes.documents.add.map(deviceDocument),
    remove: changes.documents.remove,
  });
  if ('refused' in media || 'refused' in documents) return { ok: false, code: 'cap' };
  // The row holds the same media array as the detail, so the same fold keeps the two in step.
  const row = lot.row.media ?? (changes.media.add.length > 0 ? [] : undefined);
  const rowFold = row ? applyLotMediaChanges<InventoryMediaItem>(row, mediaChanges) : null;
  const rowMedia = rowFold && 'next' in rowFold ? rowFold.next : row;
  return {
    ok: true,
    lot: {
      ...lot,
      detail: {
        ...lot.detail,
        ...('next' in media ? { media: media.next } : {}),
        ...('next' in documents ? { documents: documents.next } : {}),
      },
      row: rowMedia ? { ...lot.row, media: rowMedia } : lot.row,
    },
  };
}

/**
 * A create's staged photos and documents, split from the refs the server can take (BL-2447 AC 1):
 * a file picked offline is not in storage yet, so it leaves the create and is queued as its own add
 * once the lot exists. A ref already uploaded (picked while online) stays on the create.
 *
 * @param input.payload - the create's lot fields, as the form sent them.
 * @param input.at - now, ISO 8601: a staged document's upload time.
 * @returns the payload to queue, and the staged adds (each named by its staged file's id).
 */
export function splitOfflineCreateFiles(input: { payload: LotCreateRequestPayload; at: string }): {
  payload: LotCreateRequestPayload;
  changes: OfflineAttachmentChanges;
} {
  const { stagedMedia, stagedDocuments, ...fields } = input.payload;
  const media = stagedMedia ?? [];
  const documents = stagedDocuments ?? [];
  const keptMedia = media.filter((ref) => stagedFileIdOf(ref.storagePath) == null);
  const keptDocs = documents.filter((ref) => stagedFileIdOf(ref.storagePath) == null);
  const payload: LotCreateRequestPayload = {
    ...fields,
    ...(keptMedia.length > 0 ? { stagedMedia: keptMedia } : {}),
    ...(keptDocs.length > 0 ? { stagedDocuments: keptDocs } : {}),
  };
  return {
    payload,
    changes: {
      media: { add: media.flatMap((ref) => stagedMediaAdd(ref)), remove: [] },
      documents: { add: documents.flatMap((ref) => stagedDocumentAdd(ref, input.at)), remove: [] },
    },
  };
}

function stagedMediaAdd(ref: StagedMediaRef): OfflineMediaAdd[] {
  const fileId = stagedFileIdOf(ref.storagePath);
  return fileId == null ? [] : [{ ...ref, id: fileId }];
}

function stagedDocumentAdd(ref: StagedDocumentRef, at: string): OfflineDocumentAdd[] {
  const fileId = stagedFileIdOf(ref.storagePath);
  return fileId == null ? [] : [{ ...ref, id: fileId, uploadedAt: at }];
}
